"use client";

import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useLocale, useTranslations } from "next-intl";

const COOKIE_NAME = "poonpipat_cookie_consent";

interface CookieConsentState {
  necessary: boolean;
  analytics: boolean;
}

// Helper functions for cookie management
const setCookie = (name: string, value: string, days: number) => {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "") + expires + "; path=/";
};

const getCookie = (name: string): string | null => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

export default function CookieConsent() {
  const t = useTranslations("CookiesPolicy");
  const locale = useLocale();

  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState<CookieConsentState>({
    necessary: true,
    analytics: false,
  });

  useEffect(() => {
    const consentCookie = getCookie(COOKIE_NAME);
    if (!consentCookie) {
      setShowBanner(true);
    } else {
      try {
        const parsedConsent = JSON.parse(consentCookie);
        setConsent(parsedConsent);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        setShowBanner(true);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const newConsent = { necessary: true, analytics: true };
    setConsent(newConsent);
    setCookie(COOKIE_NAME, JSON.stringify(newConsent), 365);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSaveSettings = () => {
    setCookie(COOKIE_NAME, JSON.stringify(consent), 365);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConsent({
      ...consent,
      [event.target.name]: event.target.checked,
    });
  };

  if (!showBanner) {
    return null;
  }

  return (
    <>
      <Paper
        square
        elevation={8}
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          p: 2,
          zIndex: (theme) => theme.zIndex.modal + 1,
          backgroundColor: "background.paper",
        }}
        data-aos="fade-up"
        className="cookie-consent"
      >
        <Container>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            sx={{
              alignItems: "center",
              justifyContent: "space-between"
            }}>
            <Typography variant="body1">
              {t("Content.p1")}{" "}
              <Link href={`/${locale}/cookies-policy`} underline="always">
                {t("Hero.title")}
              </Link>
            </Typography>
            <Stack direction="row" spacing={2} sx={{
              flexShrink: 0
            }}>
              <Button variant="outlined" onClick={() => setShowSettings(true)}>
                {t("Content.cookieSettingsTitle")}
              </Button>
              <Button variant="contained" onClick={handleAcceptAll} color="primary">
                {t("Content.acceptAll")}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Paper>
      <Dialog open={showSettings} onClose={() => setShowSettings(false)} className="cookie-consent-dialog" maxWidth="md" fullWidth>
        <DialogTitle>{t("Content.cookieSettingsTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {t("Content.cookieSettingsContent")}
          </Typography>
          <Box sx={{
            mt: 2
          }}>
            <FormControlLabel
              control={<Switch checked disabled />}
              label={
                <Box>
                  <Typography variant="h3">{t('Content.strictlyNecessaryTitle')}</Typography>
                  <Typography variant="body1" dangerouslySetInnerHTML={{ __html: t.raw('Content.strictlyNecessaryCookies') }} />
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={consent.analytics}
                  onChange={handleToggle}
                  name="analytics"
                />
              }
              label={
                <Box sx={{
                  mt: 3
                }}>
                  <Typography variant="h3">{t('Content.analyticCookiesTitle')}</Typography>
                  <Typography variant="body1" dangerouslySetInnerHTML={{ __html: t.raw('Content.analyticCookies') }} />
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{p: 3}}>
          <Button onClick={handleSaveSettings} variant="contained" color="primary">
            {t("Content.saveSettings")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}